/**
 * SEE-1350: SSO account verification — flow state and shell bridge.
 *
 * Unconditional: silent SSO (the shared browser jar) plus a post-login gate
 * that verifies the authenticated account against what the user typed. There
 * is no feature flag — every branch fails open to today's behavior instead, so
 * there is no remote config to read, race, or misconfigure. Rolling this back
 * is a web deploy: the shell keeps its private-jar default and only switches
 * to the shared jar when this code explicitly asks (see notifySsoMode).
 *
 * The "switch account" flow survives full-page redirects via a sessionStorage
 * marker: the mismatch screen writes it, sign-out navigates away, and the
 * login page auto-relaunches the SSO attempt from the marker on mount.
 */
import { addBreadcrumb, captureMessage } from '@sentry/react';
import { isMobileAppShell } from 'app/auth/nativePasskeyBridge';
import { startSsoAttempt } from 'app/auth/ssoAttemptLog';

const SWITCH_MARKER_KEY = 'speedchain.ssoSwitch';
const SWITCH_MARKER_TTL_MS = 10 * 60 * 1000;

/** CustomEvent the shell dispatches into the WebView when its account-switch
 * sheet chain (Microsoft sign-out → Cognito logout) has finished. */
export const SSO_SWITCH_DONE_EVENT = 'SSO_SWITCH_DONE_FROM_NATIVE';

/** Sent by the shell the moment it begins the chain. Its absence is how we
 * detect a shell build that has no SSO_ACCOUNT_SWITCH handler. */
export const SSO_SWITCH_STARTED_EVENT = 'SSO_SWITCH_STARTED_FROM_NATIVE';

export const MICROSOFT_UNIVERSAL_LOGOUT_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/logout';

export interface SsoSwitchMarker {
  typedEmail: string;
  provider: string;
  /** The wrong account we are trying to move away from (display identity). */
  expectIdentity?: string;
  /** 'switching' = sign-out in progress, relaunch on login mount;
   *  'retried' = the relaunch already ran — a same-account return is repeat-B. */
  phase: 'switching' | 'retried';
  startedAt: number;
}

export function readSsoSwitchMarker(): SsoSwitchMarker | null {
  try {
    const raw = sessionStorage.getItem(SWITCH_MARKER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SsoSwitchMarker;
    if (!parsed?.typedEmail || !parsed?.provider) return null;
    if (Date.now() - parsed.startedAt > SWITCH_MARKER_TTL_MS) {
      clearSsoSwitchMarker();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeSsoSwitchMarker(marker: SsoSwitchMarker): void {
  try {
    sessionStorage.setItem(SWITCH_MARKER_KEY, JSON.stringify(marker));
  } catch {
    // Storage unavailable — the switch degrades to a manual retry.
  }
}

export function clearSsoSwitchMarker(): void {
  try {
    sessionStorage.removeItem(SWITCH_MARKER_KEY);
  } catch {
    // ignore
  }
}

interface WindowWithBridge extends Window {
  ReactNativeWebView?: { postMessage: (message: string) => void };
}

function postToShell(payload: Record<string, unknown>): boolean {
  if (typeof window === 'undefined') return false;
  const bridge = (window as WindowWithBridge).ReactNativeWebView;
  if (!bridge) return false;
  try {
    bridge.postMessage(JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

/**
 * Tell the shell how the NEXT SSO attempt should run. Sent right before every
 * federatedSignIn. Old shells ignore the message and keep their built-in
 * ephemeral default, so rollout order is safe in both directions.
 */
export function notifySsoMode(ephemeral: boolean): void {
  if (!isMobileAppShell()) return;
  postToShell({ type: 'SSO_MODE', ephemeral });
}

/**
 * Ask the shell to run the account-switch sheet chain (Microsoft sign-out →
 * Cognito logout). Resolves true when the shell reports completion, false on
 * timeout or when no shell/handler exists (callers fall back to the web flow).
 */
export function requestShellAccountSwitch(ackTimeoutMs = 8_000, doneTimeoutMs = 120_000): Promise<boolean> {
  if (!isMobileAppShell()) return Promise.resolve(false);
  return new Promise((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout>;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      window.removeEventListener(SSO_SWITCH_DONE_EVENT, onDone);
      window.removeEventListener(SSO_SWITCH_STARTED_EVENT, onStarted);
      clearTimeout(timer);
      resolve(ok);
    };
    const onDone = () => finish(true);
    // Two-stage wait. The chain itself is human-paced — the user works through
    // Microsoft's sign-out page and a Cognito sheet — so once the shell
    // confirms it started, allow the long window. Until then keep it short: a
    // shell build without the handler never answers, and blocking the "Taking
    // you to Microsoft…" screen for two minutes on that build is worse than
    // falling through to the web path in a few seconds.
    const onStarted = () => {
      clearTimeout(timer);
      timer = setTimeout(() => finish(false), doneTimeoutMs);
    };
    timer = setTimeout(() => finish(false), ackTimeoutMs);
    window.addEventListener(SSO_SWITCH_DONE_EVENT, onDone);
    window.addEventListener(SSO_SWITCH_STARTED_EVENT, onStarted);
    if (!postToShell({ type: 'SSO_ACCOUNT_SWITCH' })) finish(false);
  });
}

/**
 * True when a rejection came back as the SAME wrong account a switch attempt
 * just tried to move away from — relaunching again would loop, so the screens
 * escalate to Microsoft's own sign-out page (web only; the shell's sheet
 * chain already clears the cached sessions).
 */
export function isRepeatSwitchReturn(returnedIdentity: string | undefined): boolean {
  if (!returnedIdentity) return false;
  const marker = readSsoSwitchMarker();
  return (
    marker?.phase === 'retried' &&
    !!marker.expectIdentity &&
    marker.expectIdentity.toLowerCase() === returnedIdentity.toLowerCase()
  );
}

export function openMicrosoftSignOutPage(): void {
  window.open(MICROSOFT_UNIVERSAL_LOGOUT_URL, '_blank', 'noopener');
}

export function logSsoMismatch(
  event: 'shown' | 'continued' | 'switch' | 'repeat' | 'back',
  extra: Record<string, unknown>
): void {
  const message = `SSO account mismatch ${event}`;
  addBreadcrumb({ category: 'sso', message, level: 'info', data: extra });
  captureMessage(message, { level: 'info', tags: { sso_event: `mismatch_${event}` }, extra });
}

/**
 * Login-page mount half of the switch flow: if a switch is in progress, mark
 * it retried and immediately relaunch the SSO attempt with the originally
 * typed email. Returns true when a relaunch was started (the caller's login
 * page should show its SSO-redirect state).
 */
export function relaunchSsoFromSwitchMarker(federatedSignIn: (provider: string) => void): boolean {
  const marker = readSsoSwitchMarker();
  if (!marker || marker.phase !== 'switching') return false;
  writeSsoSwitchMarker({ ...marker, phase: 'retried' });
  startSsoAttempt({ provider: marker.provider, email: marker.typedEmail });
  federatedSignIn(marker.provider);
  return true;
}
